import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Globals } from 'app/_globals';
import { Pagination } from 'app/_models/pagination.model';
import { WorkerService } from 'app/_services/worker.service';
import { SortEvent } from 'primeng/api';
import { NotificationService } from 'app/_services/notification.service';
import Swal from 'sweetalert2';
import { environment } from 'environments/environment';
import { InviteUsersService } from 'app/_services/invite-users.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-user-search',
  templateUrl: './user-search.component.html',
})
export class UserSearchComponent implements OnInit {
  public role: any;
  private routeRole: any;


  // display
  public users = [];
  public columnOptionsSidebarVisible: boolean;
  public displayUserModal: boolean = false;
  public selectedUser: any;
  public isFilterLoading: boolean = false;

  public togglableColumns = [
    { displayName: 'Contractor', default: false },
    { displayName: 'Created By', default: false },
    { displayName: 'Created Date', default: false },
    { displayName: 'Last Updated By', default: true },
    { displayName: 'Last Updated Date', default: true },
    { displayName: 'Activated', default: true },
  ];

  // table options
  public tableOptions = {
    visibleColumns: [],
  }

  //filter fields
  private email: string;
  private lastName: string;
  private firstName :string;
  private phoneNumber: string;
  private contractor: string;

  // pagination - reduced page sizes - since this could be a drain on performance
  public pageSizes: any = environment.perPageOptionsSmall
  public pagination: Pagination;
  @ViewChild('paginator', { static: true }) paginator;

  // Sorting and Filtering
  public defaultSortField = 'user.lastName';
  public defaultSortOrder = 1;
  private sortOrderToText = (sortOrder: number) => { return sortOrder === 1 ? 'asc' : 'desc'; }
  public selectedSort = [this.defaultSortField + ',' + this.sortOrderToText(this.defaultSortOrder)];

  // Skeleton loading
  public loading: boolean = true;

  constructor(
    private workerService: WorkerService,
    private notificationService: NotificationService,
    private inviteUsersService: InviteUsersService,
    private router: Router,
    globals: Globals,
  ) {
    this.role = globals.user.role;
    this.routeRole = globals.user.routeRole;
  }

  ngOnInit(): void {
    this.setupColumnOptions();
    this.resetPagination(); // this will call service to populate table
  }

  public getUserList() {
    this.loading = true;

    this.workerService.getAllUsers(this.email, this.firstName, this.lastName, this.phoneNumber, this.contractor, this.pagination, this.selectedSort)
    .pipe(finalize(() => setTimeout(() => { this.loading = false; }, 500)))
    .subscribe((data: any) => {
      this.isFilterLoading = false;
      this.users = data.body;
      this.pagination.totalRecords = Number(data.totalRecords);
    }, (error) => {
      console.error(error);
      this.notificationService.notify('danger', 'Error retrieving user list');
    });
  }

  public async onRowSelect(event) {
    this.selectedUser = event.data;
    this.displayUserModal = true;
  }

  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getUserList();
  }

  private resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
    this.goToFirstPage();
    this.getUserList();
  }

  public resetColumnOptions() {
    this.tableOptions = {
      visibleColumns: this.togglableColumns.filter(col => col.default).map(col => col.displayName),
    };

    this.saveTableOptions();
  }

  public saveTableOptions() {
    localStorage.setItem('ui.options.userSearch', JSON.stringify(this.tableOptions));
  }

  public softDeleteWorkerUser(skylarkUserId){
    Swal.fire({
      title: 'Soft Delete Worker User?',
      text: 'Are you sure?',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete!',
    }).then((result) => {
      if (result.value) {
        this.workerService.softDeleteWorkerUserBySkylarkId(skylarkUserId).subscribe(result => {
          this.resetPagination();
          this.notificationService.notify('success', 'Worker User soft deleted ');
        });
      }
    });
  }

  public goToWorker(skylarkUserId) {
    this.router.navigate([this.routeRole, 'workers', skylarkUserId], {
      queryParams: { backLink: `/${this.routeRole}/workers` }
    });
  }

  public goToCompany(user) {
    const maintenanceId = user.maintenanceId;
    this.router.navigate(['/', this.routeRole, 'contractors', maintenanceId, 'details']);
  }

  public setupColumnOptions() {
    const savedOptions = localStorage.getItem('ui.options.userSearch');

    if (savedOptions) {
      this.tableOptions = JSON.parse(savedOptions);
      this.togglableColumns
        .filter(col => col.default && !this.tableOptions.visibleColumns.includes(col.displayName))
        .forEach(col => this.tableOptions.visibleColumns.push(col.displayName));
      this.saveTableOptions();
    } else {
      this.resetColumnOptions();
    }
  }
  public onFilterTextChanged(filterText: string, filterField: string){
    this.isFilterLoading = true;
    if (filterField === 'fname'){
      this.firstName = filterText;
    } else if (filterField === 'lname'){
      this.lastName = filterText;
    } else if (filterField === 'email'){
      this.email = filterText;
    } else if (filterField === 'phoneNumber'){
      this.phoneNumber = filterText;
    } else if (filterField === 'contractor'){
      this.contractor = filterText;
    }
    this.goToFirstPage();
    this.getUserList();
  }

  public customSort(sort: SortEvent) {
    const sortDirection = this.sortOrderToText(sort.order);
    this.selectedSort = [sort.field + ',' + sortDirection];

    this.goToFirstPage();

    this.getUserList();
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }

  public resendInvite(user) {
    if (user.isSendingInvite || user.inviteSent) {
      return;
    }

    user.isSendingInvite = true;

    this.inviteUsersService.resendInvite(user.login).subscribe(
      (response) => {
        user.isSendingInvite = false;
        user.inviteSent = true;
        this.notificationService.notify('success', 'Invite resent to ' + user.email);
      },
      (error) => {
        user.isSendingInvite = false;
        this.notificationService.notify('danger', 'Error', 'Failed to resend invite to ' + user.email);
      }
    );
  }

  public goToInviteUsers() {
    this.router.navigate([`/${this.routeRole}/invite-users`]);
  }

}
